import { createFileRoute, Link } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { and, desc, eq } from "drizzle-orm";
import { ArrowLeft, Download, Gauge } from "lucide-react";
import { getDb } from "@/db";
import { cars, events, runs } from "@/db/schema";
import { calculateMph, formatMph } from "@/lib/speed";

const getCarDetail = createServerFn({ method: "GET" })
	.inputValidator((data: { eventId: string; carId: string }) => data)
	.handler(async ({ data }) => {
		const db = getDb();
		const event = await db
			.select()
			.from(events)
			.where(eq(events.id, Number(data.eventId)))
			.get();
		if (!event) throw new Error("Event not found");

		const car = await db
			.select()
			.from(cars)
			.where(and(eq(cars.id, Number(data.carId)), eq(cars.eventId, event.id)))
			.get();
		if (!car) throw new Error("Car not found");

		const carRuns = await db
			.select()
			.from(runs)
			.where(eq(runs.carId, car.id))
			.orderBy(desc(runs.createdAt));

		return { event, car, runs: carRuns };
	});

export const Route = createFileRoute("/events/$eventId/cars/$carId")({
	loader: ({ params }) =>
		getCarDetail({ data: { eventId: params.eventId, carId: params.carId } }),
	component: CarDetailPage,
});

function formatTime(ms: number | null): string {
	if (ms == null) return "--";
	return (ms / 1000).toFixed(3) + "s";
}

function CarDetailPage() {
	const { event, car, runs: carRuns } = Route.useLoaderData();
	const validRuns = carRuns.filter(
		(r) => r.status === "valid" && r.elapsedTimeMs != null,
	);
	const bestTime =
		validRuns.length > 0
			? Math.min(...validRuns.map((r) => r.elapsedTimeMs!))
			: null;
	const avgTime =
		validRuns.length > 0
			? validRuns.reduce((a, r) => a + r.elapsedTimeMs!, 0) / validRuns.length
			: null;

	return (
		<div className="min-h-screen bg-background p-6">
			<div className="max-w-3xl mx-auto">
				<Link
					to="/events/$eventId"
					params={{ eventId: String(event.id) }}
					className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground text-sm mb-4 transition-colors"
				>
					<ArrowLeft size={16} />
					Back to {event.name}
				</Link>

				<div className="bg-card rounded-xl p-6 mb-6">
					<div className="flex flex-col sm:flex-row gap-6">
						{(car.avatarUrl || car.photoUrl) && (
							<div className="flex flex-wrap gap-4 shrink-0">
								{car.avatarUrl && (
									<div>
										<div className="w-32 h-32 rounded-xl overflow-hidden bg-muted">
											<img
												src={car.avatarUrl}
												alt={`${car.carName ?? "Car"} avatar`}
												className="w-full h-full object-cover"
											/>
										</div>
										<a
											href={car.avatarUrl}
											download={`${car.carName ?? "car"}-avatar.png`}
											className="inline-flex items-center gap-1 mt-2 text-xs text-primary hover:text-accent transition-colors"
										>
											<Download size={12} />
											Download Avatar
										</a>
									</div>
								)}
								{car.photoUrl && (
									<div>
										<div className="w-32 h-32 rounded-xl overflow-hidden bg-muted">
											<img
												src={car.photoUrl}
												alt={`${car.carName ?? "Car"} photo`}
												className="w-full h-full object-cover"
											/>
										</div>
										<a
											href={car.photoUrl}
											download={`${car.carName ?? "car"}-photo.png`}
											className="inline-flex items-center gap-1 mt-2 text-xs text-primary hover:text-accent transition-colors"
										>
											<Download size={12} />
											Download Photo
										</a>
									</div>
								)}
							</div>
						)}
						<div className="flex-1">
							<h1 className="text-3xl text-foreground">
								{car.carNumber && (
									<span className="inline-flex items-center text-sm font-bold text-primary bg-primary/10 rounded px-2 py-1 mr-2 align-middle">
										#{car.carNumber}
									</span>
								)}
								{car.carName}
							</h1>
							<p className="text-foreground/80 text-lg">{car.driverName}</p>
							{car.weightOz != null && (
								<p
									className={`text-sm mt-2 ${
										car.weightOz > event.maxWeightOz
											? "text-destructive font-bold"
											: "text-success"
									}`}
								>
									{car.weightOz} {event.weightUnit}
									{car.weightOz > event.maxWeightOz
										? ` (over ${event.maxWeightOz} ${event.weightUnit} limit!)`
										: ""}
								</p>
							)}
						</div>
					</div>

					<div
						className={`grid gap-4 mt-6 ${event.trackLengthFt ? "grid-cols-4" : "grid-cols-3"}`}
					>
						<div className="bg-muted rounded-lg p-4 text-center">
							<p className="text-muted-foreground text-xs uppercase font-bold">
								Best
							</p>
							<p className="text-foreground font-display text-xl font-bold">
								{formatTime(bestTime)}
							</p>
						</div>
						<div className="bg-muted rounded-lg p-4 text-center">
							<p className="text-muted-foreground text-xs uppercase font-bold">
								Avg
							</p>
							<p className="text-foreground font-display text-xl font-bold">
								{formatTime(avgTime)}
							</p>
						</div>
						{event.trackLengthFt && (
							<div className="bg-muted rounded-lg p-4 text-center">
								<p className="text-muted-foreground text-xs uppercase font-bold">
									<Gauge size={12} className="inline mr-1" />
									Top Speed
								</p>
								<p className="text-foreground font-display text-xl font-bold">
									{formatMph(calculateMph(event.trackLengthFt, bestTime))}
								</p>
							</div>
						)}
						<div className="bg-muted rounded-lg p-4 text-center">
							<p className="text-muted-foreground text-xs uppercase font-bold">
								Runs
							</p>
							<p className="text-foreground font-display text-xl font-bold">
								{carRuns.length}
							</p>
						</div>
					</div>
				</div>

				<h2 className="text-lg text-foreground mb-4">Run History</h2>
				{carRuns.length === 0 ? (
					<p className="text-muted-foreground">No runs recorded yet.</p>
				) : (
					<div className="space-y-2">
						{carRuns.map((run, i) => (
							<div
								key={run.id}
								className={`flex items-center justify-between bg-card/50 border rounded-lg px-4 py-3 ${
									run.status === "invalid"
										? "border-destructive/50 opacity-60"
										: run.status === "dnf"
											? "border-status-finals/50 opacity-60"
											: "border-border"
								}`}
							>
								<div className="flex items-center gap-4">
									<span className="text-muted-foreground text-sm font-display w-8">
										#{carRuns.length - i}
									</span>
									<span className="text-muted-foreground text-sm">
										Lane {run.lane}
									</span>
									{run.weightAtRunOz != null && (
										<span className="text-muted-foreground text-xs">
											{run.weightAtRunOz} {event.weightUnit}
										</span>
									)}
								</div>
								<div className="flex items-center gap-3">
									<span className="text-foreground font-display font-bold">
										{formatTime(run.elapsedTimeMs)}
									</span>
									{run.status !== "valid" && (
										<span
											className={`text-xs font-bold uppercase px-2 py-0.5 rounded ${
												run.status === "dnf"
													? "bg-status-finals text-foreground"
													: "bg-destructive text-foreground"
											}`}
										>
											{run.status}
										</span>
									)}
								</div>
							</div>
						))}
					</div>
				)}
			</div>
		</div>
	);
}
